import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import axios from "axios";
import Swal from "sweetalert2";
import { SDLSUrl } from "../../../config";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  if (!token) return null;
  return {
    Authorization: `Bearer ${token.replace(/"/g, "")}`,
    "Content-Type": "application/json",
  };
};

const MapDeviceModal = ({ open, onClose, device, onSuccess }) => {
  const [customerId, setCustomerId] = useState(device?.CustomerId ? String(device.CustomerId) : "");
  const [loanId, setLoanId] = useState(device?.LoanId ? String(device.LoanId) : "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    if (!device?.Imei1) return;
    setError("");
    setLoading(true);

    try {
      const headers = getAuthHeaders();
      const imei1 = device.Imei1;

      // 1. Map to Customer if provided
      if (customerId) {
        const custNum = parseInt(customerId, 10);
        if (isNaN(custNum)) {
          setError("Customer ID must be a valid number");
          setLoading(false);
          return;
        }

        const mapCustQuery = `
          mutation {
            mapDeviceToCustomer(imei1: "${imei1}", customerId: ${custNum}) {
              results
              error
            }
          }
        `;
        const custRes = await axios.post(SDLSUrl.uri, { query: mapCustQuery }, { headers });
        if (custRes.data?.data?.mapDeviceToCustomer?.error) {
          const errPayload = JSON.parse(custRes.data.data.mapDeviceToCustomer.error);
          throw new Error(errPayload.Error || errPayload.message || "Failed to map customer");
        }
      }

      // 2. Map to Loan if provided
      if (loanId) {
        const loanNum = parseInt(loanId, 10);
        if (isNaN(loanNum)) {
          setError("Loan ID must be a valid number");
          setLoading(false);
          return;
        }

        const mapLoanQuery = `
          mutation {
            mapDeviceToLoan(imei1: "${imei1}", loanId: ${loanNum}) {
              results
              error
            }
          }
        `;
        const loanRes = await axios.post(SDLSUrl.uri, { query: mapLoanQuery }, { headers });
        if (loanRes.data?.data?.mapDeviceToLoan?.error) {
          const errPayload = JSON.parse(loanRes.data.data.mapDeviceToLoan.error);
          throw new Error(errPayload.Error || errPayload.message || "Failed to map loan");
        }
      }

      await Swal.fire({
        icon: "success",
        title: "Device Mapped!",
        text: `Device IMEI ${imei1} successfully updated.`,
        timer: 2000,
        showConfirmButton: false,
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error("Mapping error:", err);
      setError(err.message || "Failed to map device.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h5" fontWeight={700}>
          Map Device to Customer / Loan
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Device Primary IMEI"
            value={device?.Imei1 || ""}
            fullWidth
            disabled
            variant="filled"
          />
          <TextField
            label="Model"
            value={device?.ModelName || "—"}
            fullWidth
            disabled
            variant="filled"
          />
          <TextField
            label="Customer ID"
            type="number"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            fullWidth
            placeholder="Enter customer ID to assign"
            helperText="Assigns this hardware to a customer record"
          />
          <TextField
            label="Loan ID"
            type="number"
            value={loanId}
            onChange={(e) => setLoanId(e.target.value)}
            fullWidth
            placeholder="Enter loan application/disbursement ID"
            helperText="Finalizes full lock linkage once loan is disbursed"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={loading} color="neutral" variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading}
          variant="contained"
          color="secondary"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? "Saving..." : "Save Linkage"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MapDeviceModal;
